/**
 * AccordionTitleBackgroundControl — the Accordion block's "Title Background"
 * editor, rebuilt on the app's STANDARD fill control (Gordon 2026-09-15,
 * web-dev /builder/4908, accordion-block-settings-cleanup): the SAME
 * <SegmentFillPanel> + <SegmentColor> composition Segment Fill
 * (SegmentFillUnified) uses — Colors / Gradient / Patterns / Image tabs with
 * the brand swatches, the real Preset / Custom / Wheel Palette / Segment
 * Palette colour tabs, and the universal Media Library for images — instead
 * of the bespoke tab-strip + ColorField picker (AccordionHeaderBackgroundControl,
 * retired by this change).
 *
 * No Opacity control (removed per spec: it also silently mis-rendered as
 * "11100%" because opacityToSlider() assumes a 0..1 fraction but the redux
 * default seeds headerOpacity as the bare integer 100).
 * No "Auto-contrast title" toggle: the title + dropdown icon always
 * auto-contrast against the header background (see accordionHeaderBackground.js
 * autoTitleTone, applied unconditionally by Accordian/index.jsx) — it is never
 * optional.
 * No "Apply to all Accordion headers in this Segment" toggle: each header is
 * styled individually.
 *
 * The `headerFill` shape (type: color/gradient/pattern/image/gif) already
 * matches SegmentFillPanel's own `fill` model 1:1 (both resolve through the
 * canonical buildSegmentFillCss), so no shape translation is needed beyond the
 * "color" type, which this control maps onto the legacy `headerBgColor` field.
 */

import React from "react";
import SegmentFillPanel from "../../Shared/SegmentStyling/SegmentFillPanel";
import GradientBuilderPanel from "../../Shared/SegmentStyling/GradientBuilderPanel";
import SegmentColor from "../../../SegmentSettings/children/SegmentColor";
import { normalizeHeaderFill } from "./accordionHeaderBackground";

const DEFAULT_COLOR = "#D4E2F2"; // legacy Title Background default

const AccordionTitleBackgroundControl = ({
  headerBgColor,
  headerFill,
  onPatch = () => {},
}) => {
  const fill = normalizeHeaderFill(headerFill) || {
    type: "color",
    color: headerBgColor || DEFAULT_COLOR,
  };

  const handleFillChange = (nextFill) => {
    if (nextFill.type === "color") {
      onPatch({ headerBgColor: nextFill.color, headerFill: null });
    } else {
      onPatch({ headerFill: nextFill });
    }
  };

  const handleGradientChange = (gradient) =>
    onPatch({ headerFill: { type: "gradient", gradient } });

  return (
    <div data-accordion-title-background>
      <SegmentFillPanel
        fill={fill}
        onChange={handleFillChange}
        segmentName="Accordion title"
        heading="Title Background"
        subheading="Choose how this header is filled."
        colorPanel={
          <SegmentColor
            showTabs={false}
            showSuggestedColorPalette
            currentColor={fill.type === "color" ? fill.color : headerBgColor || DEFAULT_COLOR}
            onColorChange={(color) => onPatch({ headerBgColor: color, headerFill: null })}
          />
        }
      />
      {fill.type === "gradient" && (
        <GradientBuilderPanel gradient={fill.gradient} onChange={handleGradientChange} />
      )}
    </div>
  );
};

export default AccordionTitleBackgroundControl;
